/**
 * Builds an "in this article" table of contents from the headings of the
 * referenced article content. Assigns ids to headings that lack one, renders
 * an anchor list, and smooth-scrolls on click. Hides itself when the content
 * has no headings so the layout can collapse to a single column.
 */
export default class BlogPostToc extends HTMLElement {
  connectedCallback() {
    const selector = this.getAttribute('content') || '.blog-post-content';
    const content = document.querySelector(selector);
    const list = this.querySelector('.blog-post-toc__list, ul');
    if (!content || !list) {
      this.hidden = true;
      return;
    }

    // Articles aren't consistent about heading level — some use h2, some h3.
    // Build the list from the shallowest heading level actually present.
    let headings = [];
    for (const level of ['h2', 'h3', 'h4']) {
      const found = content.querySelectorAll(level);
      if (found.length > 0) {
        headings = [...found];
        break;
      }
    }
    if (headings.length === 0) {
      this.hidden = true;
      return;
    }

    const used = new Set();
    const fragment = document.createDocumentFragment();

    headings.forEach((heading) => {
      const text = heading.textContent.trim();
      if (!text) return;

      if (!heading.id) {
        heading.id = this.#uniqueId(this.#slugify(text), used);
      }
      used.add(heading.id);

      const item = document.createElement('li');
      const link = document.createElement('a');
      link.href = `#${heading.id}`;
      link.textContent = text;
      link.addEventListener('click', (event) => {
        event.preventDefault();
        heading.scrollIntoView({ behavior: 'smooth', block: 'start' });
        history.replaceState(null, '', `#${heading.id}`);
      });
      item.appendChild(link);
      fragment.appendChild(item);
    });

    list.replaceChildren(fragment);
    this.hidden = false;
  }

  #slugify(text) {
    return (
      text
        .toLowerCase()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '') || 'section'
    );
  }

  #uniqueId(base, used) {
    let id = base;
    let suffix = 2;
    while (used.has(id) || document.getElementById(id)) {
      id = `${base}-${suffix}`;
      suffix += 1;
    }
    return id;
  }
}

if (!customElements.get('blog-post-toc')) {
  customElements.define('blog-post-toc', BlogPostToc);
}
